// import { Cell } from './cell.js'
// import { Clue } from './clue.js'
'use strict'

export { Cell } from './cell.js'
export { Clue } from './clue.js'

export class Grid {
  constructor ({
    cells = [],
    key = null,
    layout = null,
    metadata = {}
  } = {}
  ) {
    this.cells = cells
    this.key = key
    this.layout = layout
    this.metadata = metadata
  }

  /**
   * Get a cell based on x/y/z coordinates.
   *
   * @param x [Number] x coordinate
   * @param y [Number] y coordinate
   * @param z [Number] z coordinate (optional)
   *
   * @return [Cell] if found
   */
  cell (x, y, z) {
    let cell, foundCell
    z = z === undefined ? 0 : z

    for (let i = 0, len = this.cells.length; i < len; i++) {
      cell = this.cells[i]
      if (cell.x === x && cell.y === y && cell.z === z) {
        foundCell = cell
        break
      }
    }

    return foundCell
  }

  /**
   * Gets cells based on x/y/z coordinates.
   *
   * @param coords [Array<Number, Number, Number>] x/y/z coordinate array
   *
   * @return [<Cell...>] if found
   */
  cellsByCoords (coords) {
    let cells = []
    for (let cellCoords of coords) {
      cells.push(
        this.cell(cellCoords[0], cellCoords[1], cellCoords[2])
      )
    }

    return cells
  }

  /**
   * Looks up a cell and returns a boolean denoting whether
   * it's null or has a block cell type.
   *
   * @param x [Number] x coordinate
   * @param y [Number] y coordinate
   * @param z [Number] z coordinate (optional)
   *
   * @return [Boolean] denoting whether the cell is available,
   *  or is a block.
   */
  cellIsNullOrBlock (x, y, z) {
    let cell = this.cell(x, y, z)

    return !cell || cell.cellType === 'block'
  }

  /**
   * Looks up a cell and returns a boolean denoting whether
   * it contains an answer.
   *
   * @param x [Number] x coordinate
   * @param y [Number] y coordinate
   * @param z [Number] z coordinate (optional)
   *
   * @return [Boolean] denoting whether the cell has an answer.
   */
  cellHasAnswer (x, y, z) {
    let cell = this.cell(x, y, z)

    return cell && cell.answer
  }

  /**
   * Get a cell based on idx.
   *
   * @param idx [Number] one-based index number.
   *
   * @return [Cell]
   */
  cellByIdx (idx) {
    let cell, foundCell

    for (let i = 0, len = this.cells.length; i < len; i++) {
      cell = this.cells[i]

      if (cell.idx === idx) {
        foundCell = cell
        break
      }
    }

    return foundCell
  }

  /**
   * Calculates an idx value based on the passed coordinates.
   *
   * @note `idx` is zero-based.
   *
   * @param x [Number] an integer between 0 and grid width
   * @param y [Number] an integer between 0 and grid height
   * @param z [Number] an integer between 0 (optional)
   *
   * @return [Integer] the calculated idx value
   */
  idxFromCoords (x, y, z) {
    return x + (y * this.width)
  }

  /**
   * Calculates coordinates from cell index.
   *
   * @note `idx` is zero-based.
   *
   * @return [Hash] x/y/z
   */
  coordsFromIdx (idx) {
    const y = Math.floor(idx / this.height)
    const x = idx - (y * this.width)
    const z = 0

    return {
      x: x,
      y: y,
      z: z
    }
  }

  /**
   * Get clue numbers and coordinates.
   *
   * @return [Array<Number, Number, Number>] cell number, x coordinate, y coordinate
   */
  clueNumbersWithCoords () {
    let nums = []
    this.cells.sort(function (a, b) {
      return a.idx - b.idx
    }).map(function (cell) {
      // Allow for zero-based cell numbers.
      if (cell.cellNumber !== null) {
        nums.push([ cell.cellNumber, cell.x, cell.y, cell.z ])
      }
    })

    return nums
  }

  /**
   * Sorts all current cells by their `idx`.
   *
   * Should re-sort on this.cells change.
   *
   * @param cells [Array[<Cell>]]
   *
   * @return [Array[<Cell>]]
   */
  sortCellsByIdx (cells) {
    return cells.sort(function (a, b) {
      return a.idx - b.idx
    })
  }

  // Getters and Setters

  get cells () {
    return this['_cells']
  }

  set cells (value) {
    this['_cells'] = this.sortCellsByIdx(value)
    return this['_cells']
  }
}



//////////////////
// WEBPACK FOOTER
// ./~/pzl-tools/lib/grid.js
// module id = 87
// module chunks = 0